import { useEffect, useState } from "react";
import { Bot, Pause, Play, RefreshCw, PhoneForwarded, Loader2, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const fallbackVoice = { master_enabled: false, agency_enabled: false, provider_ready: false, due_followups: 0 };
const fallbackSophia = { approved: false };

export default function AIBacklogStatus() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sophia, setSophia] = useState(null);
  const [busy, setBusy] = useState(false);
  const canManage = ["admin", "ops_manager"].includes(user?.role);

  const load = async () => {
    const [v, s] = await Promise.allSettled([api.get("/voice-ai/auto/status"), api.get("/sophia/status")]);
    setData(v.status === "fulfilled" ? v.value.data : fallbackVoice);
    setSophia(s.status === "fulfilled" ? s.value.data : fallbackSophia);
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!canManage || !sophia?.approved) return;
    setBusy(true);
    try {
      await api.put("/voice-ai/auto/automation", { enabled: !data?.agency_enabled });
      toast.success(data?.agency_enabled ? "AI backlog calling paused" : "AI backlog calling enabled");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update AI backlog calling"); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    if (!canManage || !data?.agency_enabled || !sophia?.approved) return;
    setBusy(true);
    try {
      const { data: result } = await api.post("/voice-ai/auto/run-now");
      toast.success(`${result.started || 0} AI follow-up call(s) started`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI backlog cycle could not run"); }
    finally { setBusy(false); }
  };

  if (!data || !sophia) return <div className="mb-4 h-20 animate-pulse rounded-xl border bg-muted/40" />;
  const locked = !sophia.approved;
  const active = data.master_enabled && data.agency_enabled && !locked;

  return <div className="mb-4 rounded-xl border border-[#315A7D]/25 bg-[#315A7D]/5 p-4" data-testid="ai-backlog-status">
    <div className="flex flex-wrap items-center gap-3">
      <div className="grid h-10 w-10 place-items-center rounded-full bg-[#17324D] text-white"><Bot className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2"><div className="font-heading font-bold">AI Follow-Up Backlog</div><Badge className={active ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : locked ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>{active ? "Automatic" : locked ? "Sophia approval required" : data.master_enabled ? "Paused" : "Needs deployment switch"}</Badge><Badge variant="outline">{data.due_followups || 0} due</Badge></div>
        <div className="mt-1 text-xs text-muted-foreground">{active ? `No per-call click required · up to ${data.batch_size || 0} per cycle · ${Math.round((data.interval_seconds || 0) / 60)} min cycle · ${data.call_window?.start ?? 9}:00–${data.call_window?.end ?? 20}:00 local` : locked ? "Automatic calling stays locked until Sophia's voice is heard, her knowledge is reviewed, and she is explicitly approved." : "Automatic calling stays fail-closed until the deployment switch and agency setting are enabled."}</div>
        {locked && <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-800"><LockKeyhole className="h-3.5 w-3.5" />Sophia acceptance lock is active.</div>}
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground"><span className={data.provider_ready ? "text-emerald-700" : "text-amber-700"}>{data.provider_ready ? "OpenAI + Twilio ready" : "OpenAI/Twilio setup required"}</span><span className={data.transfer_configured ? "text-emerald-700" : "text-amber-700"}><PhoneForwarded className="mr-1 inline h-3 w-3" />{data.transfer_configured ? "Live transfer ready" : "Transfer number not configured"}</span></div>
      </div>
      {canManage && <div className="flex gap-2"><Button variant="outline" size="sm" onClick={toggle} disabled={busy || !data.master_enabled || locked} data-testid="ai-backlog-toggle">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : active ? <Pause className="mr-1.5 h-4 w-4" /> : <Play className="mr-1.5 h-4 w-4" />}{active ? "Pause" : "Enable"}</Button><Button size="sm" onClick={runNow} disabled={busy || !active || !data.provider_ready || locked} data-testid="ai-backlog-run-now"><RefreshCw className="mr-1.5 h-4 w-4" /> Work Backlog Now</Button></div>}
    </div>
  </div>;
}
